import clone from 'clone'
import { DateTime, Duration } from 'luxon'
import { ScaleLabeling, ChartData, EnergyData, PriceData, RelayData, ChartType, PowerData, ProfitData, EnergySummary, ConsumptionStats } from '../types/charts'
import { TimeString, UserSettings } from '../types/models'
import { first, mean, median, sma } from './math'
import { lang } from './lang'
// import { billing } from './billing'

/**
 * Function to use for combining multiple data points to single data point.
 */
export type SmootheningType = 'none' | 'median' | 'mean' | 'sma'

/**
 * Options for series manipulation.
 */
export interface SeriesOptions {
  type?: ChartType
  compact?: boolean // Use shorter labels.
  from: TimeString // Starting timestamp of the time range wanted.
  includeEmpty?: boolean, // If set, include also data point for time slots without any sample data.
  includeLast?: boolean, // If set, include also last stamp to the range.
  k?: number, // Algorithm parameter `k`.
  key?: string, // Name of the field of interest to calculate from data.
  n?: number, // Algorithm parameter `n`.
  panX?: boolean // If set, include all values instead limiting to the range.
  smoothening: SmootheningType // Function to clean data.
  sparse?: boolean // Try less labels instead of all.
  steps?: ScaleLabeling // Time ranges to group data points together.
  to: TimeString // Final timestamp of the time range wanted.
  timeKey?: 'from' | 'timestamp', // Name of the field containing time stamps. (Default to 'timestamp')
  timestamps?: boolean // Do not construct labels, but use milliseconds since Epoch instead.
  utc?: boolean // Show labels in UTC (useful for testing mainly).
  variant?: 'primary' | 'secondary', // Color theme.
  vat?: number, // VAT for price related calculations.
  user?: UserSettings, // Personal billing settings.
}

/**
 * Incoming data point.
 */
export type SeriesItem = {
  timestamp: number
  [key: string]: number | null
}

/**
 * Grouped data points forming a single output entity.
 */
export type SeriesGroup = {
  from: number
  to: number
  members: SeriesItem[]
}

/**
 * Conversion function from group of data points to single data point.
 */
export type SmootheningFunction = (group: SeriesGroup, options: SeriesOptions) => SeriesItem[]

/**
 * Result structure returned by simplify() function.
 */
export type SimplifyResult = {
  labels: number[] | string[]
  result: number[],
  groups: SeriesGroup[],
  range: number[],
  startIndex: number,
  endIndex: number,
}

/**
 * Result structure returned by relay() function.
 */
export type RelayResult = {
  spot: number,
  size: number,
  startEdge: boolean,
  stopEdge: boolean,
  color?: string,
  dimmed?: boolean
}

/**
 * Utility collection for manipulating numeric data series for chart drawing.
 */
export const Series = {

  /**
   * Find the exact start and end point for the data range.
   * IMPORTANT: We now adjust "first" and "last" timestamps to Finnish time zone for ranges greater than hour, so that time labels and later-filtered data are displayed correctly in charts.
   */
  minmax: function(data: ChartData | ProfitData[], options: SeriesOptions): [number, number] {

    const { from, to, panX } = options
    let steps = options.steps
    const timeKey = options.timeKey || 'timestamp'

    let first: TimeString = panX ? data[0][timeKey as 'timestamp'] as TimeString : from
    let last: TimeString = panX ? (data[data.length - 1][timeKey as 'timestamp'] as TimeString > to ? data[data.length - 1][timeKey as 'timestamp'] as TimeString : to) : to
    let t0, t1

    // Fix time zoneless UTC stamps.
    if (!/Z$/.test(first)) {
      first = (first + 'Z') as TimeString
    }
    if (!/Z$/.test(last)) {
      last = (last + 'Z') as TimeString
    }

    if (steps !== undefined) {

      const utcOffsetStr = DateTime.fromISO(first).setZone('Europe/Helsinki').toFormat('ZZ')

      let diffKey: keyof Duration
      switch (steps) {
        case 'minute':
          first = first.substring(0, 16) + ':00.000Z' as TimeString
          diffKey = 'minutes'
          break
        case 'quarter':
          steps = 'hour'
          first = first.substring(0, 13) + ':00:00.000Z' as TimeString
          diffKey = 'hours'
          break
        case 'hour':
          first = first.substring(0, 13) + ':00:00.000Z' as TimeString
          diffKey = 'hours'
          break
        case 'day':
          first = first.substring(0, 10) + 'T00:00:00.000' + utcOffsetStr as TimeString
          diffKey = 'days'
          break
        case 'week':
          t0 = first.substring(0, 10) + 'T00:00:00.000' + utcOffsetStr
          t1 = last.substring(0, 10) + 'T00:00:00.000' + utcOffsetStr

          t0 = DateTime.fromISO(t0).plus({ days: -(DateTime.fromISO(t0).weekday - 1) }).toUTC().toISO()
          t1 = DateTime.fromISO(t1).plus({ days: 7 - DateTime.fromISO(t1).weekday }).toUTC().toISO()

          return [DateTime.fromISO(t0 as string).toMillis(), DateTime.fromISO(t1 as string).toMillis()]
        case 'month':
          first = first.substring(0, 7) + '-01T00:00:00.000' + utcOffsetStr as TimeString
          diffKey = 'months'
          break
        case 'year':
          first = first.substring(0, 4) + '-01-01T00:00:00.000' + utcOffsetStr as TimeString
          diffKey = 'years'
          break
        default:
          throw new Error(`Time step '${steps}' not supported in Series.minmax().`)
      }
      const diff = DateTime.fromISO(last).diff(DateTime.fromISO(first), diffKey)
      last = DateTime.fromISO(first).plus({ [steps]: Math.ceil(diff[diffKey]) }).toUTC().toISO() as TimeString
    }
    return [DateTime.fromISO(first).toMillis(), DateTime.fromISO(last).toMillis()]
  },

  /**
   * Generate a range of steps as time stamps fully covering a time range.
   */
  range: function(from: number, to: number, options: SeriesOptions): number[] {
    const { steps } = options

    if (options.steps === undefined) {
      return [from, to]
    }

    let addition

    switch (steps) {
      case 'quarter':
        addition = { minutes: 15 }
        break
      case 'minute':
      case 'hour':
      case 'day':
      case 'week':
      case 'month':
      case 'year':
        addition = { [steps as string]: 1 }
        break
      default:
        throw new Error(`Time step '${steps}' not supported in Series.range().`)
    }

    const last = DateTime.fromMillis(to)
    const ret: number[] = []
    for (let stamp = DateTime.fromMillis(from); stamp <= last; stamp = stamp.plus(addition)) {
      ret.push(stamp.toMillis())
    }

    return ret
  },

  /**
   * Combine data points according to the time slot they belong.
   */
  collect: function(range: number[], data: ChartData, options: SeriesOptions): SeriesGroup[] {
    const timeKey = options.timeKey || 'timestamp'

    // Speed up trivial case.
    if (options.steps === undefined) {
      return [{
        from: range[0],
        to: range[1],
        members: data as SeriesItem[]
      }]
    }

    // Initialize collection.
    const values = new Array(range.length - 1)
    for (let i = 0; i < range.length - 1; i++) {
      values[i] = { from: range[i], to: range[i + 1], members: [] }
    }

    // Collect values per slot.
    let r = 1
    let v = 0
    while (r <= range.length && v < data.length) {
      while (v < data.length && data[v][timeKey] as number < range[r]) {
        values[r - 1].members.push(data[v])
        v++
      }
      r++
    }

    return values
  },

  /**
   * Apply a function to group of numbers combining them to single value.
   */
  apply: function(data: SeriesGroup[], options: SeriesOptions): SeriesItem[] {

    const { key, smoothening, includeEmpty } = options

    const calc = Series.smootheningFunction(smoothening)

    // Append in place: `result = result.concat(items)` copies the whole
    // accumulated array once per group, which is quadratic in the group count
    // and OOMs Safari when a fine scale buckets a long series into hundreds of
    // thousands of groups.
    const result: SeriesItem[] = []

    for (const group of data) {
      const items = calc(group, options)
      if (items.length === 0 && includeEmpty) {
        result.push({
          timestamp: (group.from + group.to) / 2,
          [key || '']: null
        })
      }
      for (const item of items) {
        result.push(item)
      }
    }

    return result
  },

  /**
   * Trim timestamps to more usable labels trimming them smaller and using local time.
   * @param stamps
   */
  labels: function(stamps: number[], options: Partial<SeriesOptions>): string[] {

    const { steps } = options

    switch (steps) {

      case 'minute':
        return stamps.map((s, idx) => {
          if (!s) {
            return ''
          }
          const stamp: string = options.utc ? DateTime.fromMillis(s).toUTC().toISO() as string : DateTime.fromMillis(s).toLocal().toISO() as string
          const time = stamp.substring(14, 16)
          return options.compact ? time.substring(0, 4) : stamp.substring(11, 16)
        })

      case 'quarter':
        return stamps.map((s) => {
          if (!s) {
            return ''
          }
          // Group timestamps from mean() are at the midpoint of a 15-min slot;
          // subtract 7m30s to label by slot start.
          const base = options.utc ? DateTime.fromMillis(s).toUTC() : DateTime.fromMillis(s).toLocal()
          const aligned = base.minus({ minutes: 7, seconds: 30 })
          return options.compact ? aligned.toFormat('mm') : aligned.toFormat('HH:mm')
        })
      case 'hour':
        return stamps.map((s, idx) => {
          if (!s) {
            return ''
          }
          let label = ''
          const stamp: string = options.utc ? DateTime.fromMillis(s).toUTC().toISO() as string : DateTime.fromMillis(s).toLocal().toISO() as string
          const time = stamp.substring(11, 16)
          if (!options.compact && (idx === 0 || (idx > 3 && time === '00:00'))) {
            const date = stamp.substring(5, 10)
            label = `${date} ${time}`
          } else {
            label = options.compact ? time.substring(0, 2) : time
          }
          return options.sparse && idx % 2 === 1 ? '' : label
        })

      case 'day':
        return stamps.map((s, idx) => {
          if (!s) {
            return ''
          }
          const stamp: DateTime = options.utc ? DateTime.fromMillis(s).toUTC() : DateTime.fromMillis(s).toLocal()
          return lang.dayNameShort(stamp.weekday)
        })

      case 'week':
        return stamps.map((s, idx) => {
          if (!s) {
            return ''
          }
          const stamp: DateTime = options.utc ? DateTime.fromMillis(s).toUTC() : DateTime.fromMillis(s).toLocal()
          return `${stamp.weekNumber}`
        })

      case 'month':
        return stamps.map((s, idx) => {
          if (!s) {
            return ''
          }
          const stamp: DateTime = options.utc ? DateTime.fromMillis(s).toUTC() : DateTime.fromMillis(s).toLocal()
          return lang.monthNameShort(stamp.month)
        })

      case 'year':
        return stamps.map((s, idx) => {
          if (!s) {
            return ''
          }
          const stamp: DateTime = options.utc ? DateTime.fromMillis(s).toUTC() : DateTime.fromMillis(s).toLocal()
          return `${stamp.year}`
        })

      default:
        throw new Error(`Time steps '${steps}' not supported in Series.labels().`)
    }
  },

  /**
   * Process chart data to smaller pieces.
   */
  simplify: function(data: ChartData, options: SeriesOptions): SimplifyResult {

    const { key, timestamps, includeLast, panX } = options
    const timeKey = options.timeKey || 'timestamp'

    if (data.length === 0 && panX) {
      return {
        labels: [],
        result: [],
        groups: [],
        range: [],
        startIndex: 0,
        endIndex: 0,
      }
    }

    const [min, max] = Series.minmax(data, options)
    const range = Series.range(min, max, options)
    const data0 = Series.numericStamps(data, options).sort((a: any, b: any) => a[timeKey] - b[timeKey])
    const delta = range[range.length - 1] - range[range.length - 2]
    if (includeLast) {
      range.push(range[range.length - 1] + delta)
    }

    const groups = Series.collect(range, data0, options)
    const applied = Series.apply(groups, options)
    const stamps = applied.map(e => e.timestamp)

    const labels = timestamps ? stamps : Series.labels(stamps, options)

    if (data.length === 0) {
      return {
        labels: options.includeEmpty ? labels : [],
        result: [],
        groups: [],
        range: [],
        startIndex: 0,
        endIndex: 0,
      }
    }

    const result = applied.map(e => e[key || ''] as number)

    let startIndex, endIndex
    if (panX) {
      const from = new Date(options.from).getTime()
      const to = new Date(options.to).getTime()

      startIndex = applied.findIndex(e => e.timestamp >= from)
      if (startIndex < 0) {
        startIndex = 0
      }

      endIndex = applied.findIndex(e => e.timestamp > to)
      if (endIndex < 0) {
        endIndex = result.length - 1
      } else {
        endIndex--
      }
    } else {
      startIndex = 0
      endIndex = result.length - 1
    }

    return {
      labels,
      result,
      groups,
      range,
      startIndex,
      endIndex
    }
  },

  /**
   * Return smoothening function by its name.
   */
  smootheningFunction(name: SmootheningType): SmootheningFunction {
    switch (name) {
      case 'mean':
        return mean
      case 'median':
        return median
      case 'sma':
        return sma
      case 'none':
        return first
      default:
        console.warn(`No implementation for smoothening '${name}'.`)
        return mean
    }
  },

  /**
   * Convert timestamps to numeric.
   */
  numericStamps(data: ChartData, options: SeriesOptions): ChartData {
    const timeKey = options.timeKey || 'timestamp'
    return data.map((e: any) => ({ ...e, [timeKey]: DateTime.fromISO(e[timeKey]).toMillis() }))
  },

  /**
   * Convert timestamps from price data to milliseconds.
   */
  timestampedPrices(prices: PriceData[]): PriceData<number>[] {
    return prices.map(e => ({
      price: e.price,
      from: DateTime.fromISO(e.from).toMillis(),
      to: DateTime.fromISO(e.to).toMillis()
    }))
  },

  /**
   * Collect relay bar drawing instructions from relay information.
   * @param data
   */
  relay(data: RelayData<number>[], from: TimeString, to: TimeString, options: Partial<SeriesOptions> = {}): RelayResult[] {
    const toStamp = DateTime.fromISO(to).toMillis()
    const fromStamp = DateTime.fromISO(from).toMillis()
    const range = toStamp - fromStamp
    const now = Math.min(new Date().getTime(), toStamp + 1)

    const result: RelayResult[] = []

    let lastStamp: null | number = null
    let last: null | boolean = null

    const sorted = clone(data).sort((a, b) => a.timestamp - b.timestamp)

    // Helper to get state for entries.
    const state = (i: number): boolean => {
      return !!sorted[i].is_on
    }

    // Scan for initial value.
    for (let i = 0; i < sorted.length && sorted[i].timestamp < fromStamp; i++) {
      if (sorted[i].source === 'device') {
        lastStamp = sorted[i].timestamp
        last = state(i)
      }
    }
    // Helper to add either normal, dimmed or split to both bar.
    const addBar = (a: number, b: number) => {
      if (b < fromStamp || a > toStamp) {
        return
      }

      a = Math.max(a, fromStamp)
      b = Math.min(b, toStamp)

      if (now >= a && now <= b) {
        result.push({
          spot: 100 * ((a - fromStamp) / range),
          size: 100 * ((now - a) / range),
          startEdge: true,
          stopEdge: false
        })
        // If part ends right now, leave the first part "open".
        if (now < b) {
          result.push({
            spot: 100 * ((now - fromStamp) / range),
            size: 100 * ((b - now) / range),
            startEdge: false,
            stopEdge: true,
            dimmed: true
          })
        }

      } else {

        result.push({
          spot: 100 * ((a - fromStamp) / range),
          size: 100 * ((b - a) / range),
          startEdge: true,
          stopEdge: true,
          dimmed: a > now
        })
      }
    }

    for (let i = 0; i < sorted.length; i++) {
      // Construct a bar
      if (sorted[i].source !== 'action' && state(i) !== last) {
        if (!state(i)) {
          if (lastStamp !== null) {
            addBar(lastStamp, sorted[i].timestamp)
          }
        }
        lastStamp = sorted[i].timestamp
        last = state(i)
      }
    }

    // Make a bar if the relay has been left on
    if (last && lastStamp !== null) {
      addBar(lastStamp, toStamp)
    }

    return result
  },

  /**
   * Find total energy accumulated during the period in kWh.
   *
   * The `energy` field is a cumulative counter (joules) that only grows, so
   * the total is the sum of the deltas between consecutive samples. A negative
   * delta means the device rebooted and its counter reset mid-range; such
   * discontinuities are skipped instead of being subtracted, so a reboot no
   * longer turns the whole range total negative. For ranges without a reset
   * the sum of deltas telescopes to exactly `last - first`, matching the old
   * behavior. Samples with a missing (null/NaN) energy value are ignored.
   */
  totalEnergy(data: EnergyData[], options: Partial<SeriesOptions> = {}): number {
    let total = 0
    let prev: number | null = null
    for (const point of data) {
      const energy = point.energy
      if (energy === null || energy === undefined || !isFinite(energy)) {
        continue
      }
      if (prev !== null) {
        const delta = energy - prev
        if (delta > 0) {
          total += delta
        }
      }
      prev = energy
    }
    return total / 3.6e6
  },

  /**
   * Calculate indicators from the energy data that is sorted by date.
   */
  averages(data: EnergyData[] | PowerData[], prices: PriceData[], options: Partial<SeriesOptions> = {}): {
    consumption: number | null,
    price: number | null,
    avgPrice: number | null,
    avgMarketPrice: number | null,
  } {

    const { vat } = options

    // Prepare prices so that they can be found easily for each the hour timestamps.
    const priceMap: Record<number, number | undefined> = {}
    const priceTimestamps: number[] = []
    for (const price of prices) {
      const timestamp = new Date(price.from).getTime()
      priceTimestamps.push(timestamp)
      priceMap[timestamp] = price.price
    }

    // Collect values.
    let consumption: number | null = null
    let price: number | null = null
    let avgMarketPrice: number | null = null
    let avgPrice: number | null = null

    // Don't bother calculate yet if we don't have prices available.
    // TODO: We could still calculate consumption even if we don't know prices.
    if (prices.length === 0 || data.length < 2) {
      return { consumption, price, avgPrice, avgMarketPrice }
    }

    let marketCount = 0
    consumption = 0
    price = 0
    avgMarketPrice = 0

    // Calculate market price averages.
    for (let i = 0; i < priceTimestamps.length; i++) {
      avgMarketPrice += (prices[i].price || 0) / 1000
      marketCount++
    }

    avgMarketPrice = marketCount ? avgMarketPrice / marketCount : null

    // Each data point is a 1-hour bucket (API defaults to steps=hour).
    // Previously this was inferred from the first two data points, which gave
    // wrong results when there was a data gap at the start of the range.
    const timeScale = 1

    // Calculate consumption.
    for (let i = 0; i < data.length; i++) {
      const time = data[i].timestamp.substring(0, 13) + ':00:00Z'
      const stamp = new Date(time).getTime()

      // Sum up the entries in the range.
      const add = data[i].power * timeScale / 1000
      consumption += add

      // Find the price and add.
      if (priceMap[stamp] !== undefined) {
        price += (priceMap[stamp] || 0) * add / 1000
        // Alternatively: price += billing(add, stamp, user || {}, priceMap, true)
      } else {
        console.error(`There is no price available for hour starting at ${time}.`)
      }
    }

    avgPrice = price === null || !consumption ? null : price / consumption

    if (vat) {
      return {
        consumption,
        price: price * (1 + vat),
        avgPrice: avgPrice ? avgPrice * (1 + vat) : avgPrice,
        avgMarketPrice: avgMarketPrice === null ? null : avgMarketPrice * (1 + vat),
      }
    }
    return {
      consumption,
      price,
      avgPrice,
      avgMarketPrice,
    }
  },

  /**
   * Derive consumption-meter display stats from a server-side energy summary.
   *
   * Import and export are billed separately, so VAT is applied to the imported
   * energy only — never to the export revenue. Because the averages are
   * computed from same-signed sums, they are genuine weighted averages and
   * cannot blow up the way a single netted-period average can.
   */
  consumptionStats(summary: EnergySummary | null, vat: number, includeFees = false): ConsumptionStats {
    if (summary === null) {
      return {
        importEnergy: null,
        exportEnergy: null,
        avgImportPrice: null,
        avgExportPrice: null,
        netPrice: null,
        avgMarketPrice: null,
      }
    }

    const importEnergy = summary.import_energy
    const exportEnergy = summary.export_energy
    // VAT lifts the import cost (and therefore the average import price).
    // Margin and transfer fee already have their VAT.
    const importCost = summary.import_cost * (1 + vat) + (includeFees ? summary.import_fees : 0)
    // Export revenue is paid without VAT.
    const exportRevenue = summary.export_revenue

    return {
      importEnergy,
      exportEnergy,
      avgImportPrice: importEnergy ? importCost / importEnergy : null,
      avgExportPrice: exportEnergy ? exportRevenue / exportEnergy : null,
      netPrice: importCost - exportRevenue,
      // With the fee switch on, the market price gets the same margin and transfer fee as the imports.
      avgMarketPrice: summary.avg_market_price === null
        ? null
        : summary.avg_market_price * (1 + vat) + (includeFees ? summary.avg_fees ?? 0 : 0),
    }
  },
}
